/**
 * 1.8.0 genealogy: Made from grouped by source (the Related tab and the asset's Rights tab). Pure.
 * A source is its asset when it still has one, else its snapshot (code and title, a purged source), else the
 * built-in graphic template.
 */
import type { AssetSourceDTO, GenealogyKind } from '@prism/contracts';

export interface MadeFromGroup {
  key: string;
  assetId: string | null;
  title: string;
  code: string | null;
  kind: GenealogyKind;
  purged: boolean;
  /** The tracks it is on, in the order first met (V1, A2…). */
  tracks: string[];
  uses: AssetSourceDTO[];
}

const KIND_TITLES: Record<GenealogyKind, string> = { footage: 'Footage', audio: 'Sound', still: 'Still', graphic: 'Graphic' };

/** The key a use is grouped by. */
export function sourceKey(u: AssetSourceDTO): string {
  if (u.source.assetId) return `a:${u.source.assetId}`;
  if (u.source.code || u.source.title) return `s:${u.source.code ?? ''}:${u.source.title ?? ''}`;
  if (u.graphicTemplate) return `g:${u.graphicTemplate.id}`;
  return `u:${u.id}`;
}

/** The uses grouped by source, in the order the sources are first used. */
export function groupMadeFrom(uses: readonly AssetSourceDTO[]): MadeFromGroup[] {
  const groups = new Map<string, MadeFromGroup>();
  for (const u of uses) {
    const key = sourceKey(u);
    let g = groups.get(key);
    if (!g) {
      const title = u.source.title ?? (u.graphicTemplate ? `Graphic template ${u.graphicTemplate.id}` : KIND_TITLES[u.kind]);
      g = { key, assetId: u.source.assetId, title, code: u.source.code, kind: u.kind, purged: u.source.purged, tracks: [], uses: [] };
      groups.set(key, g);
    }
    if (!g.tracks.includes(u.track)) g.tracks.push(u.track);
    g.uses.push(u);
  }
  return [...groups.values()];
}

/** The hidden count (sources and made assets the reader can't see): "1 restricted asset not shown". */
export function hiddenText(n: number): string {
  return `${n} restricted asset${n === 1 ? '' : 's'} not shown`;
}

/**
 * The recycle and purge dialogs' line about a source's genealogy: the assets made with it keep the record of that use
 * when it is purged (asset_sources keeps the snapshot). Nothing when it is used in none.
 */
export function usedInText(n: number | null | undefined): string | null {
  if (!n) return null;
  return `Used in ${n === 1 ? '1 asset' : `${n} assets`}; the record of that use is kept.`;
}
